import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { Dropdown, Form, Header, Icon, Segment } from 'semantic-ui-react';
import SurveyService from '~/api/services/survey';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteTypes } from '~/routes/constants';
import { El } from '~/components/Layout/style';
import { StyledRating } from '../CreateEditQuestion/QuestionForms/Stars';
import { formatNumberWithoutFractions, formatNumbers, handleErrors } from '~/utils/helpers';
import { MaxValueLabel, MinValueLabel, SliderC, SliderWrapper } from '../CreateEditQuestion/QuestionForms/Slider';
import { StyledAnswer, StyledSegmentWrapper } from '../../ResultsSurvey/style';
import { useAuth } from '~/routes/components/AuthProvider';

const AddQuestions: React.FC = () => {
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const { id } = useParams()
  const navigate = useNavigate()
  const { systemData: { logged: { languageId } } } = useAuth()
  const [listOfQuestions, setListOfQuestions] = useState([])
  const [surveyList, setSurveyList] = useState([])
  const [surveySelected, setSurveySelected] = useState(null)
  const [questionsImport, setQuestionsImport] = useState([])
  const [loadingQuestionData, setLoadingQuestionData] = useState(false)

  useEffect(() => {
    fetchSurveysList()
  }, [t])

  useEffect(() => {
    if (surveySelected) {
      fetchQuestionList()
    }
  }, [surveySelected])

  async function fetchQuestionList() {
    setLoadingQuestionData(true)
    try {
      const responseQuestions = await SurveyService.getSurveyQuestionList(surveySelected)
      setListOfQuestions(responseQuestions.data)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingQuestionData(false)
  }

  async function fetchSurveysList() {
    setIsBusy(true);
    try {
      const responeSurveyList = await SurveyService.getAllSurveys(id)
      const surveyIsInList = Boolean(responeSurveyList.data.find(survey => survey.id === id))
      setSurveyList(responeSurveyList.data)
      setSurveySelected(surveyIsInList ? id : '')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  async function handleAddingQuestion(questions) {
    setLoadingQuestionData(true)
    try {
      if (questions.length > questionsImport.length) {
        const idQuestion = questions.slice(-1)
        const response = await SurveyService.showSurveyQuestion(surveySelected, idQuestion)
        setQuestionsImport([...questionsImport, { ...response.data }])
      } else {
        const removedItem = questionsImport.find((questionImport) => !questions.find(question => question === questionImport.id))
        if (removedItem) {
          setQuestionsImport(questionsImport.filter(question => question?.id !== removedItem?.id))
        }
      }
    } catch (error) {
      handleErrors(error)
    }
    setLoadingQuestionData(false)
  }

  function renderQuestions() {
    return <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
      <Dropdown
        placeholder={t('Select survey')}
        noResultsMessage={t('No results found....')}
        options={surveyList.map(survey => ({ value: survey?.id, text: survey?.title }))}
        onChange={(e, data) => setSurveySelected(data.value)}
        value={surveySelected}
        selection
        search
        selectOnBlur={false}
      />
      <Dropdown
        placeholder={t('Select questions')}
        noResultsMessage={t('No results found....')}
        options={listOfQuestions.map(question => ({ value: question?.id, text: question?.question, key: question?.id }))}
        //@ts-ignore
        onChange={(e, data) => handleAddingQuestion(data.value)}
        clearable
        value={questionsImport.map(questionImport => questionImport?.id)}
        selection
        search
        disabled={loadingQuestionData}
        loading={loadingQuestionData}
        multiple
        selectOnBlur={false}
      />
    </div>
  }

  async function handleImportQuestions() {
    setIsBusy(true);
    try {
      await SurveyService.importQuestionList(id, { questions: questionsImport.map(questionImport => questionImport.id) })
      notify(t('Successfully imported questions'), 'success');
      navigate(RouteTypes.SURVEY_SHOW.replace(':id', id))
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      disabled={questionsImport.length === 0}
      onClick={() => handleImportQuestions()}
    >{t('Import questions')}
    </El.PageHeaderActionButton>
  }

  function renderAnswer(question, answer, answers = []) {
    const questionOnLoggedLanguage = question?.languages.find(questionLang => questionLang.languageId === languageId)
    switch (question?.type) {
      case 'text':
        return <Form.Input placeholder={questionOnLoggedLanguage?.placeholder} disabled />
      case 'radio':
        return answers.map(answerOption => {
          return <Form.Checkbox
            radio
            label={answerOption.answer}
            disabled
            key={answerOption.id}
          />
        })
      case 'select':
        return <Form.Dropdown
          selection
          width={8}
          label={t('Selected answers')}
          options={question?.answers?.map(answerOption => ({ text: answerOption.answer, value: answerOption.id }))}
          disabled
          required={Boolean(question?.required)}
        />
      case 'stars':
        return <>
          <StyledRating
            icon='star'
            maxRating={question?.value}
            disabled
            size={'large'}
          />
        </>
      case 'number':
        return <Form.Input
          width={8}
          label={
            t('Min - Max: {{min}} {{unit}} - {{max}} {{unit}}',
              { min: formatNumbers(question.minValue, question?.subtype), max: formatNumbers(question.maxValue, question.subtype), unit: question.unitLabel })
          }
          value={''}
          placeholder={question?.placeholder}
        />
      case 'slider':
        return <Form.Field>
          <Form.Field label={t('Rating: {{rating}} {{watchUnit}}',
            { rating: formatNumberWithoutFractions(question?.minValue), watchUnit: question?.unitLabel })} />
          <SliderWrapper>
            <MinValueLabel>{`${formatNumberWithoutFractions(question?.minValue)} ${question?.unitLabel || ''}`}</MinValueLabel>
            <SliderC
              rating={0}
              step={parseInt(question?.interval)}
              disabled
              min={question?.minValue}
              max={question?.maxValue}
              value={question?.minValue}
            />
            <MaxValueLabel>{`${formatNumberWithoutFractions(question?.maxValue)} ${question?.unitLabel || ''}`}</MaxValueLabel>
          </SliderWrapper>
        </Form.Field>
      case 'sorting':
        return <Form.Field required={Boolean(question?.required)} >
          {answers?.map((answer, index) => {
            return <StyledAnswer key={answer.id}>
              {answer.answer}
            </StyledAnswer>
          })}
        </Form.Field>
      case 'multitext':
        return <>
          <Form.Field label={questionOnLoggedLanguage?.question} required={Boolean(question?.required)} />
          {Array.from({ length: question?.value }, (_, index) => index).map((anwser) => {
            return <Form.TextArea
              key={anwser}
              placeholder={questionOnLoggedLanguage?.placeholder}
            />
          })}
        </>
      case 'checkbox':
        return answers.map(answerOption => {
          return <Form.Checkbox
            label={answerOption.answer}
            disabled
            key={answerOption.id}
          />
        })
      case 'group':
        return
      case 'multiselect':
        return <Form.Dropdown
          selection
          width={8}
          multiple
          label={t('Selected answers')}
          options={question?.answers?.map(answerOption => ({ text: answerOption.answer, value: answerOption.id }))}
          disabled
          required={Boolean(question?.required)}
        />
      default:
        return
    }
  }

  function getAnswersByLanguage(question, languageId) {
    return question?.answers?.map((answer) =>
      answer.languages.find((language) => language.id === languageId) ?? ''
    );
  }

  function renderTextQuestion(question, index) {
    const questionOnLoggedLanguage = question?.languages.find(questionLang => questionLang.languageId === languageId)
    const answers = getAnswersByLanguage(question, languageId)
    return <StyledSegmentWrapper>
      <Header attached='top' block style={{ margin: 0 }}>
        <div className='title'>{`${index + 1}. ${questionOnLoggedLanguage?.question}`}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div>{question?.typeLabel}</div>
          <Icon
            style={{ cursor: 'pointer' }}
            onClick={() => handleAddingQuestion([...questionsImport.filter(questionImport => questionImport.id !== question.id)])}
            name='trash'
          />
        </div>
      </Header>
      <Segment attached>
        <Form style={{ margin: 20, width: '100%' }}>
          {renderAnswer(question, question?.answer, answers)}
        </Form>
      </Segment>
    </StyledSegmentWrapper>
  }

  function renderQuestionsPreview() {
    return <div style={{ marginTop: '2em' }}>
      <div>{t('Questions preview')}</div>
      {questionsImport.map((questionsImport, index) => {
        return <div key={questionsImport?.id}>
          {renderTextQuestion(questionsImport, index)}
        </div>
      })}
    </div>
  }

  return <Layout
    title={t('Add questions')}
    isBusy={isBusy}
    segmented
    close={RouteTypes.SURVEY_SHOW.replace(':id', id)}
    actions={renderActions()}
  >
    {renderQuestions()}
    {renderQuestionsPreview()}
  </Layout>
}
export default AddQuestions